import { AppContext } from "01Data/AppData";
import { AndonDataType, FilterItem, FilterSetting } from "01Data/DataTypes";
import "97Parts/ScrollBarStyle.css";
import { Signal } from "99Tools/useModel";
import {
  ExpandLess,
  ExpandMore,
  FilterAlt,
  FilterAltOff,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
} from "@mui/material";
import { ANDON_HEADER_COLOR } from "Settings";
import { useContext, useState } from "react";

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: "10px",
}));

const Header = styled("th")(({ theme }) => ({
  fontSize: "0.8rem",
  borderCollapse: "collapse",
  border: "solid 1px #aaa",
  backgroundColor: ANDON_HEADER_COLOR,
  color: theme.palette.common.white,
  whiteSpace: "nowrap",
}));

const FilterTitle = styled("div")(({ theme }) => ({
  fontSize: "1.2rem",
}));

const Cell = styled("td")(({ theme }) => ({
  fontSize: "1.0rem",
  borderCollapse: "collapse",
  border: "solid 1px #aaa",
  padding: "8px",
  minWidth: "15vw",
  color: theme.palette.grey[800],
}));

const FilterItems = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
}));

const CellItems = ({ col }: { col: FilterItem }) => {
  if (col.name === "") {
    return null;
  }
  return (
    <>
      <div>{col.name}</div>
      <div>{col.bShow ? "✅" : "🟩"}</div>
    </>
  );
};

const FilterMenu = ({
  target,
  filter,
  redrawSection,
}: {
  target: AndonDataType;
  filter: FilterSetting[];
  redrawSection: Signal;
}) => {
  const andon = useContext(AppContext).GetAndonData();
  const [bShowSettings, setShowSettings] = useState(false);

  if (filter.length === 0) {
    return null;
  }

  const changeFilter = (item: FilterItem, colIndex: number) => {
    andon.Sort(andon.sortParamBuilding);
    if (item.name === "") {
      // 空欄
      return;
    } else if (item.name === "(すべて選択)") {
      // フィルターリセット
      andon.ResetFilter(target, !item.bShow, colIndex);
      redrawSection();
      return;
    }
    item.bShow = !item.bShow;

    // すべて選択のチェック状態制御
    andon.UpdateAllCheck(target, colIndex);

    andon.ApplyFilter(target);
    redrawSection();
  };

  // ヘッダ
  const HeaderRow = filter.map((item: FilterSetting) => (
    <Header key={item.name}>{item.name}</Header>
  ));

  // name 未設定の項目を除外
  filter.forEach((f) => {
    f.items = f.items.filter((i) => i.name !== "");
  });

  // 設定表の縦横入れ替え
  let nRow = 0;
  const nCol = filter.length;
  filter.forEach(
    (col: FilterSetting) =>
      (nRow = col.items.length < nRow ? nRow : col.items.length)
  );
  const matrix = new Array<FilterItem[]>(nRow);
  for (let i = 0; i < nRow; i++) {
    matrix[i] = new Array<FilterItem>(nCol);
  }
  for (let row = 0; row < nRow; row++) {
    for (let col = 0; col < nCol; col++) {
      matrix[row][col] =
        row < filter[col].items.length
          ? filter[col].items[row]
          : new FilterItem("", false);
    }
  }
  // 表作成
  const grid = matrix.map((rowItems: FilterItem[], iRow: number) => {
    const row = rowItems.map((col: FilterItem, iCol: number) => {
      const key = `row${iRow}col${iCol}`;
      return (
        <Cell
          key={key}
          onClick={() => changeFilter(col, iCol)}
          style={{ cursor: "pointer" }}
        >
          <FilterItems style={{ minWidth: "10vh" }}>
            <CellItems col={col} />
          </FilterItems>
        </Cell>
      );
    });
    return <tr key={`row${iRow}`}>{row}</tr>;
  });

  // フィルタ文字列
  let filterItems: string[] = [];
  filter.forEach((param: FilterSetting) => {
    if (param.items.find((item: FilterItem) => !item.bShow)) {
      filterItems.push(param.name);
    }
  });
  const filterTitle = filterItems.join(", ");

  return (
    <>
      <ListItemButton onClick={() => setShowSettings(!bShowSettings)}>
        <ListItemIcon>
          {filterTitle === "" ? <FilterAltOff /> : <FilterAlt />}
        </ListItemIcon>
        <ListItemText
          primary={filterTitle + "　" /*ブランク時のレイアウト崩れ防止*/}
        />
        {bShowSettings ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
      <Collapse
        sx={{ width: "100%" }}
        in={bShowSettings}
        timeout="auto"
        unmountOnExit
      >
        <Box
          sx={{ margin: "10px", padding: "10px" }}
          border={1}
          borderColor="grey.400"
          borderRadius="10px"
        >
          <FilterTitle style={{ marginBottom: "5px" }}>
            フィルタ設定
          </FilterTitle>
          <div
            className="scrollBar"
            style={{ width: "100%", overflow: "auto" }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "solid 1px #aaa",
              }}
            >
              <tbody>
                <tr key={"header"}>{HeaderRow}</tr>
                {grid}
              </tbody>
            </table>
          </div>
        </Box>
      </Collapse>
    </>
  );
};

export const AndonTitleBuilding = ({
  redrawSection,
}: {
  redrawSection: Signal;
}) => {
  const andon = useContext(AppContext).GetAndonData();
  const filter = andon.filter.buildings;

  return (
    <Title variant="h5" color={"GrayText"}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "center",
        }}
      >
        <div>建物</div>
        <FilterMenu
          target={"buildings"}
          filter={filter}
          redrawSection={redrawSection}
        />
      </div>
    </Title>
  );
};

export const AndonTitleSensor = ({
  redrawSection,
}: {
  redrawSection: Signal;
}) => {
  const andon = useContext(AppContext).GetAndonData();
  const filter = andon.filter.sensors;

  return (
    <Title variant="h5" color={"GrayText"}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "center",
        }}
      >
        <div>漏水みはり番</div>
        <FilterMenu
          target={"sensors"}
          filter={filter}
          redrawSection={redrawSection}
        />
      </div>
    </Title>
  );
};

export const AndonTitlePico3 = ({
  redrawSection,
}: {
  redrawSection: Signal;
}) => {
  const andon = useContext(AppContext).GetAndonData();
  const filter = andon.filter.pico3;

  return (
    <Title variant="h5" color={"GrayText"}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "center",
        }}
      >
        <div>Pico3</div>
        <FilterMenu
          target={"pico3"}
          filter={filter}
          redrawSection={redrawSection}
        />
      </div>
    </Title>
  );
};
